import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, OnDestroy, NgZone, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '../../service/common/app.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

import { AuthService } from '../../service/common/auth.service';
import { CommonSharedModule } from '../../shared/common/common.module';
import { TimezoneService } from '../../service/common/timezone.service';
import { MenuMapService } from '../../service/common/menu-map.service';
import { ThemeService } from '../../service/common/theme.service';
import { initialState } from '../../../store/index.reducer';
import { BiometricAuthService } from '../../service/common/biometric-auth.service';
import Swal from 'sweetalert2';

interface MenuItem {
  id: number;
  name: string;
  uuid: string;
  target: string | null;
  order_no: number;
  parent_id: number | null;
  permission_slug: string | null;
  children?: MenuItem[];
}
import { LocalStorageService } from '../../service/common/local-storage.service';
import { RouteUpdateService } from '../../service/common/route-update.service';
import { LanguageService } from '../../service/common/language.service';
import { MenuLoadService } from '../../service/common/menu-load.service';
import { forkJoin } from 'rxjs';

interface MenuItem {
  id: number;
  name: string;
  uuid: string;
  target: string | null;
  order_no: number;
  parent_id: number | null;
  permission_slug: string | null;
  children?: MenuItem[];
}

function organizeMenu(menuList: MenuItem[]): MenuItem[] {
  const itemMap = new Map<number, MenuItem>();

  menuList.forEach((item) => {
    itemMap.set(item.id, { ...item, children: [] });
  });

  menuList.forEach((item) => {
    if (item.parent_id !== null) {
      const parent = itemMap.get(item.parent_id);
      if (parent) {
        parent.children!.push(itemMap.get(item.id)!);
      }
    }
  });

  const organizedMenu = menuList
    .filter((item) => item.parent_id === null)
    .map((item) => itemMap.get(item.id)!)
    .sort((a, b) => a.order_no - b.order_no);

  return organizedMenu;
}

@Component({
  standalone: true,
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  imports: [CommonSharedModule, ReactiveFormsModule],
  animations: [
    trigger('toggleAnimation', [
      transition(':enter', [style({ opacity: 0, transform: 'scale(0.95)' }), animate('100ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))]),
      transition(':leave', [animate('75ms', style({ opacity: 0, transform: 'scale(0.95)' }))]),
    ]),
  ],
})
export class CoverLoginComponent implements OnInit, OnDestroy {
  companyId: number = 1;

  email = '';
  password = '';
  subscribe = false;

  loading = false;
  store: any = initialState;
  loginForm: FormGroup;
  isSubmitted = false;

  passwordVisible = false;
  iconClicked = false;

  title_key: string = 'login';

  showBiometricLogin = false;
  biometricLoading = false;
  biometricEmail = '';
  // Which half of the login screen is showing - once fingerprint login is
  // enabled it's the default view (mirrors standard mobile-app behaviour:
  // fingerprint first, "use password instead" as the fallback), otherwise
  // it's the only option and 'password' is the sole mode ever used.
  loginMode: 'password' | 'biometric' = 'password';
  private biometricAttempt = false;
  private readonly BIOMETRIC_DECLINED_KEY = 'biometric_login_declined';

  private focusListener: any;
  private biometricRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(
    private formBuilder: FormBuilder,
    private http: HttpClient,
    public translate: TranslateService,
    public storeData: Store<any>,
    public router: Router,
    private appSetting: AppService,
    private toastr: ToastrService,
    private authService: AuthService,
    private localstore: LocalStorageService,
    private routeUpdateService: RouteUpdateService,
    private languageService: LanguageService,
    private route: ActivatedRoute,
    private menuLoadService: MenuLoadService,
    private timezoneService: TimezoneService,
    private commonService: MenuMapService,
    private themeService: ThemeService,
    private biometricAuthService: BiometricAuthService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)]],
      password: ['', [Validators.required]],
      subscribe: [false],
    });

    let commonRememberMe: any = null;
    try {
      const rememberMeRaw = this.localstore.getData('rememberme');
      commonRememberMe = rememberMeRaw ? JSON.parse(rememberMeRaw) : null;
    } catch {
      commonRememberMe = null;
    }
    const rememberMe = commonRememberMe ? commonRememberMe.rememberMe === 'true' : false;

    if (rememberMe) {
      this.loginForm.patchValue({
        email: commonRememberMe.savedEmail || '',
        password: commonRememberMe.savedPassword || '',
        subscribe: true,
      });
    }
  }

  ngOnInit() {
    this.initStore();
    // document.documentElement.classList.add('login-page');
    this.loginForm.controls['email'].statusChanges.subscribe((status) => {
      if (this.isSubmitted) {
        this.loginForm.controls['email'].markAsTouched();
      }
    });

    this.loginForm.controls['password'].statusChanges.subscribe((status) => {
      if (this.isSubmitted) {
        this.loginForm.controls['password'].markAsTouched();
      }
    });

    this.focusListener = () => {
      this.checkIfLoggedIn();
      // Re-check on resume too, not just at mount - covers the native
      // biometric bridge not being ready yet on a cold app start.
      this.checkBiometricLoginAvailability();
    };
    window.addEventListener('focus', this.focusListener);

    this.checkBiometricLoginAvailability();
    // Guards a cold-start race where the native biometric bridge isn't fully
    // ready the instant this component mounts, so the very first check can
    // come back empty even though credentials are saved - one retry shortly
    // after covers it without needing a dedicated "bridge ready" plugin.
    this.biometricRetryTimer = setTimeout(() => this.checkBiometricLoginAvailability(), 500);
  }

  // Only auto-switch the view into biometric mode once - later re-checks (on
  // resume, on the cold-start retry) should still refresh showBiometricLogin
  // so the "use fingerprint instead" link stays accurate, but shouldn't yank
  // the user back to the fingerprint screen if they deliberately switched to
  // the password form.
  private biometricModeAutoApplied = false;

  private async checkBiometricLoginAvailability(): Promise<void> {
    if (!this.biometricAuthService.isSupported) return;
    const available = await this.biometricAuthService.isAvailable();
    if (!available) return;
    const hasSaved = await this.biometricAuthService.hasSavedCredentials();

    // Capacitor plugin calls resolve via the native bridge, outside Angular's
    // zone - without this, the state below is set correctly but the view
    // doesn't re-render until some unrelated zone-patched event (a click,
    // etc.) happens to run change detection afterwards.
    this.ngZone.run(() => {
      this.showBiometricLogin = hasSaved;
      if (hasSaved) {
        this.biometricEmail = this.biometricAuthService.getSavedEmail();
        if (!this.biometricModeAutoApplied) {
          this.loginMode = 'biometric';
          this.biometricModeAutoApplied = true;
        }
      }
      // Belt-and-suspenders: force a synchronous check pass right here rather
      // than trusting zone.js to schedule one on its own - on this build the
      // view was staying on the old template until an unrelated click forced
      // a check, which points at something not reliably re-entering the zone.
      this.safeDetectChanges();
    });
  }

  switchToPasswordLogin(): void {
    this.loginMode = 'password';
  }

  switchToBiometricLogin(): void {
    this.biometricEmail = this.biometricAuthService.getSavedEmail();
    this.loginMode = 'biometric';
  }

  async loginWithBiometrics(): Promise<void> {
    if (this.biometricLoading || this.loading) return;
    this.biometricLoading = true;
    try {
      const credentials = await this.biometricAuthService.loginWithBiometrics();
      // The native fingerprint prompt resolves via Capacitor's bridge, outside
      // Angular's zone - patchValue/onSubmit (and everything onSubmit kicks
      // off, including the HTTP call) need to run inside it, or the rest of
      // the login flow silently stops updating the view.
      this.ngZone.run(() => {
        this.loginForm.patchValue({ email: credentials.email, password: credentials.password });
        this.biometricAttempt = true;
        this.onSubmit();
        this.safeDetectChanges();
      });
    } catch (error) {
      // User cancelled the prompt or verification failed - fall back to the
      // manual form silently, no need to surface the raw plugin error.
      console.warn('Biometric login cancelled or failed:', error);
    } finally {
      this.ngZone.run(() => {
        this.biometricLoading = false;
        this.safeDetectChanges();
      });
    }
  }

  private async offerBiometricSetup(email: string, password: string): Promise<void> {
    if (!this.biometricAuthService.isSupported) return;
    const available = await this.biometricAuthService.isAvailable();
    if (!available) return;

    const alreadySaved = await this.biometricAuthService.hasSavedCredentials();
    if (alreadySaved) {
      // Keep the stored credentials in sync in case the password just changed -
      // this is a silent background refresh of an already-enrolled fingerprint,
      // not a new enrollment, so it doesn't need another scan.
      await this.biometricAuthService.saveCredentials({ email, password });
      this.ngZone.run(() => {
        this.showBiometricLogin = true;
        this.biometricEmail = email;
        this.safeDetectChanges();
      });
      return;
    }

    if (this.localstore.getData(this.BIOMETRIC_DECLINED_KEY) === 'true') return;

    const result = await Swal.fire({
      title: 'Enable Fingerprint Login?',
      text: 'Scan your fingerprint to log in faster next time.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Enable',
      cancelButtonText: 'Not now',
    });

    if (!result.isConfirmed) {
      this.localstore.storeData(this.BIOMETRIC_DECLINED_KEY, 'true');
      return;
    }

    try {
      // Standard pattern: require an actual fingerprint scan to confirm
      // enrollment, rather than saving credentials silently in the background.
      await this.biometricAuthService.enrollCredentials({ email, password });
      this.ngZone.run(() => {
        this.showBiometricLogin = true;
        this.biometricEmail = email;
        this.safeDetectChanges();
      });
    } catch (error) {
      // Scan cancelled/failed - don't mark as declined, just try again on the
      // next successful login.
      console.warn('Fingerprint enrollment cancelled or failed:', error);
    }
  }

  ngOnDestroy(): void {
    document.documentElement.classList.remove('login-page');
    window.removeEventListener('focus', this.focusListener);
    this.destroyed = true;
    if (this.biometricRetryTimer) {
      clearTimeout(this.biometricRetryTimer);
    }
  }

  // detectChanges() throws (NG0911) if the view was already torn down by the
  // time an in-flight biometric plugin call resolves (e.g. the user
  // navigated away right after a successful login) - guard every call site
  // through here instead of repeating the destroyed check everywhere.
  private safeDetectChanges(): void {
    if (this.destroyed) return;
    try {
      this.cdr.detectChanges();
    } catch {
      // View was torn down between the check above and this call - ignore.
    }
  }

  get formControls() {
    return this.loginForm.controls;
  }

  get showTenantRegistration(): boolean {
    try {
      const conf = this.localstore.getData('config');
      const enc_config = conf ? JSON.parse(conf) : null;
      return !!enc_config && (enc_config.enable_multitenant_registration === true || enc_config.enable_multitenant_registration === 'true');
    } catch {
      return false;
    }
  }

  initStore() {
    this.storeData
      .select((d) => d.index)
      .subscribe((d) => {
        this.store = d;
      });
  }

  changeLanguage(item: any) {
    this.translate.use(item.code);
    this.appSetting.toggleLanguage(item);
    if (this.store.locale?.toLowerCase() === 'ae') {
      this.storeData.dispatch({ type: 'toggleRTL', payload: 'rtl' });
      this.languageService.serviceChangeLanguage(this.companyId, item.code.toLowerCase());
    } else {
      this.storeData.dispatch({ type: 'toggleRTL', payload: 'ltr' });
      this.languageService.serviceChangeLanguage(this.companyId, item.code.toLowerCase());
    }
  }

  get emailControl() {
    return this.loginForm.get('email');
  }

  get passwordControl() {
    return this.loginForm.get('password');
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }

  toggleIcon() {
    this.iconClicked = !this.iconClicked;
  }

  onLoginSuccess(companyId: number): void {
    // Fetch the menu data first and subscribe to it
    this.menuLoadService.fetchMenuData(companyId).subscribe({
      next: (menuList) => {
        if (menuList.length > 0) {
          // Proceed with the rest of the login process

          this.routeUpdateService.addDynamicRoutes();
        } else {
          console.error('Failed to load menu, login halted');
        }
      },
      error: (error) => {
        console.error('Error fetching menu data during login:', error);
      },
    });
  }
  setConfig(companyId: number, userID: any): void {
    this.menuLoadService.fetchConfigData(companyId, userID).subscribe({
      next: (res: any) => {
        this.timezoneService.reloadConfig();
      },
      error: (error: any) => {
        console.error('Error fetching menu data during login:', error);
      },
    });
  }

  checkIfLoggedIn() {
    // Replace with your actual logic to check if user is logged in
    const userData = this.localstore.getData('user_data');
    if (userData) {
      this.router.navigate(['/dashboard']);
    }
  }

  onSubmit() {
    this.isSubmitted = true;

    if (this.loginForm.controls['email'].hasError('pattern')) {
      const key = 'form_email_valid_msg';
      const errorMessage = this.translate.instant(key);
      this.toastr.error(errorMessage, 'Error');
      return;
    }

    if (this.loginForm.invalid) {
      const key = 'Both_fields_are_required_and_valid';
      const errorMessage = this.translate.instant(key);
      this.toastr.error(errorMessage, 'Error');
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const loginData = this.loginForm.value;
    const attemptedViaBiometrics = this.biometricAttempt;
    this.biometricAttempt = false;

    this.authService
      .login({
        ...loginData,
        from_source: 1,
      })
      .subscribe({
        next: async (response: any) => {
          if (response.status) {
            await this.offerBiometricSetup(loginData.email, loginData.password);

            const userID = response.data.id;
            const activeCompanyId = Number(response.data.company_id || this.companyId);
            this.companyId = activeCompanyId;
            const companies = Array.isArray(response.data?.companies) ? response.data.companies : [];
            const activeCompany = companies.find((company: any) => Number(company?.id || company?.company_id) === activeCompanyId);
            const activeCompanyTenantId = Number(
              response.data.company_tenant_id || response.data.selected_company_tenant_id || activeCompany?.tenant_id || response.data.tenant_id || 0
            );

            const permissionsObj = response.data.permissions.reduce((acc: any, perm: any) => {
              acc[perm.slug] = perm.accessible;
              return acc;
            }, {});
            const userData = {
              main: {
                ...response.data,
                selected_company_id: activeCompanyId,
                selected_company_tenant_id: activeCompanyTenantId || response.data.selected_company_tenant_id,
              },
              permissions: permissionsObj,
              user_id: userID,
              company: {
                id: activeCompanyId,
                name: response.data.company_name || activeCompany?.name || activeCompany?.company_name,
                code: response.data.company_code || activeCompany?.code || activeCompany?.company_code,
                tenant_id: activeCompanyTenantId || activeCompany?.tenant_id,
                tenant_name: response.data.tenant_name || activeCompany?.tenant_name || activeCompany?.tenant?.name,
                tenant_code: response.data.tenant_code || activeCompany?.tenant_code || activeCompany?.tenant?.code,
              },
            };

            // Store the user data along with permissions and menu lists
            const conf: any = this.localstore.getData('config');
            let enc_config: any = null;
            try {
              enc_config = conf ? JSON.parse(conf) : null;
            } catch {
              enc_config = null;
            }
            this.localstore.storeData('base_app_url', JSON.stringify(response.data.base_app_url));
            this.localstore.storeData('version_info', JSON.stringify(response.data.version_info));
            if (response.data.theme_info) {
              const themeData = JSON.stringify(response.data.theme_info);
              this.localstore.storeData('theme_info', themeData);

              this.themeService.applyThemeFromLocalStorage();
            }
            if (enc_config != null && enc_config.encrypt_local_storage == 'true') {
              this.localstore.storeDataEncrypted('user_data', JSON.stringify(userData));
            } else {
              this.localstore.storeData('user_data', JSON.stringify(userData));
            }

            // Update permissions list in RouteUpdateService
            this.routeUpdateService.setPermissionsList(permissionsObj);

            if (this.loginForm.value.subscribe) {
              this.localstore.storeData(
                'rememberme',
                JSON.stringify({ savedEmail: this.loginForm.value.email, savedPassword: this.loginForm.value.password, rememberMe: 'true' })
              );
            } else {
              this.localstore.removeData('rememberme');
            }

            if (companies.length > 1 || this.isCompanyAdminRole(response.data?.role)) {
              this.localstore.storeData('company_selection_pending', 'true');
              this.localstore.removeData('selected_company_id');
              this.localstore.removeData('menuList');
              this.localstore.removeData('unorgmenuList');
              this.localstore.removeData('menu_id');
              localStorage.setItem('login', Date.now().toString());
              this.router.navigate(['/select-company']).then(() => {
                window.location.reload();
              });
              return;
            }

            this.localstore.removeData('company_selection_pending');
            this.localstore.storeData('selected_company_id', String(activeCompanyId));
            this.setConfig(activeCompanyId, userID);

            this.getconfig(userID);

            this.onLoginSuccess(activeCompanyId);

            // Add dynamic routes

            forkJoin([this.menuLoadService.fetchMenuData(activeCompanyId)]).subscribe({
              next: ([configData]) => {
                // Notify other tabs of login
                localStorage.setItem('login', Date.now().toString());
                this.router.navigate(['/dashboard']).then(() => {
                  window.location.reload();
                });
              },
              error: (error) => {
                console.error('Error during login:', error);
                this.toastr.error('Login failed due to an internal error.');
              },
            });
          } else {
            if (response.code === 401) {
              const errorMessage = 'Login Access Denied';
              this.toastr.error(errorMessage, 'Error');
              return;
            }
            if (response.code == 405 || response.code == 421) {
              const key = 'incorrect_username_or_password';
              const errorMessage = this.translate.instant(key);
              this.toastr.error(errorMessage, 'Error');
              if (attemptedViaBiometrics) {
                // The saved credentials no longer work (e.g. password changed
                // elsewhere) - drop them so fingerprint login doesn't keep
                // failing silently, and fall back to the manual form.
                this.biometricAuthService.clearSavedCredentials();
                this.showBiometricLogin = false;
                this.loginMode = 'password';
              }
            } else {
              const key = 'login_failed';
              const errorMessage = this.translate.instant(key);
              this.toastr.error(errorMessage, 'Error');
            }
          }
        },
        error: (error) => {
          const key = 'error';
          let errorMessage = this.translate.instant(key);
          if (error.code === 429) {
            errorMessage = error.message;
          }
          this.toastr.error(errorMessage, 'Error');
          this.loading = false;
          console.error(error);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  private isCompanyAdminRole(role: any): boolean {
    return (
      String(role || '')
        .trim()
        .toLowerCase() === 'company_admin'
    );
  }

  // Copy of getconfig from AuthLayout
  getconfig(userId?: number) {
    // Prepare params for the procedure
    const params: any = { categories: { '0': 'ac1', '1': 'ac2', '2': 'ac30', '3': 'ac12' } };
    if (userId !== undefined && userId !== null) {
      params.user_id = userId; // Add user_id if provided
      //params.categories = { '0': 'ac16', '1': 'ac17' };
    }

    const procedureParams = { proc_name: 'get_configurations_values_v1', params };

    this.commonService.unAuthProcedureCall(procedureParams).subscribe({
      next: (response: { code: number; status: boolean; data: any; message: string }) => {
        if (response.code === 200 && response.status && response.data) {
          const res = response.data?.[0]?.result?.data || {};

          if (Object.keys(res).length > 0) {
            // Optionally handle favicon, logo, etc. here if needed
            this.localstore.storeData('config', JSON.stringify(res));
          }
        } else {
          const key = 'error';
          const errorMessage = this.translate.instant(key);
          this.toastr.error(errorMessage, 'Error');
        }
      },
      error: (error) => {
        console.error('Error fetching data:', error);
      },
      complete: () => {},
    });
  }
}
